import { RESET_ALL as RESET_ON_LOGOUT } from 'redux-store/reducers/common-reducer';
import { fetchBrandProfile } from 'modules/brands/reducers/brandProfile';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';

import { isDefined } from 'utils/helper/common';
import { createReducer } from 'utils/redux';
import { API, get, put } from 'utils/api';
import { isDraft } from 'utils/subcampaign';
import { logError } from 'utils/log';
import { Querystring } from 'utils/url';
import { snakeCaseKeys } from 'utils/keys';

import type { Action, MessengerConversation, MessengerMessage, ThunkAction } from 'types';
import type { ConversationCount, ConversationsState } from '../types/ConversationsState.type';

const LOADING = 'conversations/LOADING';
const SELECTED_CONVERSATION_LOADING = 'conversations/SELECTED_CONVERSATION_LOADING';
const CONVERSATION_COUNT_LOADING = 'conversations/CONVERSATION_COUNT_LOADING';
const LOADED = 'conversations/LOADED';
const RESET = 'conversations/RESET';
const SET_LAST_MESSAGE = 'conversations/SET_LAST_MESSAGE';
const LOAD_MORE = 'conversations/LOAD_MORE';
const SELECTED_CONVERSATION_LOADED = 'conversations/SELECTED_CONVERSATION_LOADED';
const CONVERSATION_COUNT_LOADED = 'conversations/CONVERSATION_COUNT_LOADED';

const { CAMPAIGNS, MESSENGER } = API.END_POINTS;

const initialState: ConversationsState = {
  isLoading: false,
  list: [],
  hasMoreConversations: true,
  isFilterApplied: false,
  hasUnreadFilter: false,
  isSelectedConversationLoading: false,
  isConversationCountLoading: false,
  count: {
    overall: {
      all: 0,
      agreed: 0,
      pending: 0,
      revoked: 0
    },
    unread: {
      all: 0,
      agreed: 0,
      pending: 0,
      revoked: 0
    }
  }
};

function getParams(params?: { filterByType?: string, filterByState?: string, searchQuery?: string | undefined | null }):
  { search?: string, conversationState?: string, states?: Array<string> } {
  const newParams: { search?: string, conversationState?: string, states?: Array<string> } = {};

  if (params?.searchQuery) {
    newParams.search = params.searchQuery;
  }

  if (params?.filterByType && params.filterByType !== 'all') {
    newParams.conversationState = params.filterByType;
  }

  if (params?.filterByState && params.filterByState !== 'all') {
    newParams.states = [params.filterByState];
  }

  return newParams;
}

function setLoadingStatus(status: boolean, type?: string): Action {
  return {
    type: type || LOADING,
    payload: {
      status
    }
  };
}

function conversationsCountLoaded(count: ConversationCount): Action {
  return {
    type: CONVERSATION_COUNT_LOADED,
    payload: {
      count
    }
  };
}

function conversationsLoaded(
  conversations: Array<MessengerConversation>,
  hasMoreConversations: boolean,
  isFilterApplied: boolean,
  hasUnreadFilter: boolean): Action {
  return {
    type: LOADED,
    payload: {
      conversations,
      hasMoreConversations,
      isFilterApplied,
      hasUnreadFilter
    }
  };
}

export function selectedConversationLoaded(conversation: MessengerConversation | null): Action {
  return {
    type: SELECTED_CONVERSATION_LOADED,
    payload: {
      conversation
    }
  };
}

function loadMoreConversations(conversations: Array<MessengerConversation>, hasMoreConversations: boolean): Action {
  return {
    type: LOAD_MORE,
    payload: {
      conversations,
      hasMoreConversations
    }
  };
}

export function resetConversations(): Action {
  return {
    type: RESET,
    payload: {}
  };
}

export function fetchPreSelectedConversation(campaignId: number, subcampaignId?: number, conversationExternalId?: string): ThunkAction {
  return async (dispatch) => {
    let url: string;

    if (subcampaignId) {
      url = MESSENGER.getPaginatedConversations(`campaign_id=${campaignId}&subcampaign_id=${subcampaignId}`);
    } else if (conversationExternalId) {
      url = MESSENGER.getPaginatedConversations(`campaign_id=${campaignId}&conversation_uuid=${conversationExternalId}`);
    } else {
      return;
    }

    setLoadingStatus(true, SELECTED_CONVERSATION_LOADING);

    const conversationsResponse = await get(url);
    const selectedConversation: MessengerConversation | undefined = conversationsResponse.data?.data[0];

    if (!selectedConversation) {
      return;
    }

    const queryStringForSubcampaigns = Querystring.objectToString({
      include: ['business_details', 'bank_details', 'billing_address'],
      subcampaign_ids: [selectedConversation?.subcampaign.id]
    });
    const subcampaign = (await get(`${CAMPAIGNS.getCampaignSubcampaigns(campaignId)}?${queryStringForSubcampaigns}`))?.data[0];

    if (!subcampaign) {
      return;
    }

    const brandProfile = await dispatch(fetchBrandProfile(subcampaign.campaign.brandProfileId, false));
    const emptyLastMessage = {
      attachments: {},
      createdAt: new Date().toISOString(),
      externalId: '',
      metadata: { profile: { id: null, type: '' } },
      senderId: '',
      text: '',
      type: '',
      isEmpty: true
    };

    const lastMessage = selectedConversation.lastMessage || { ...emptyLastMessage };

    dispatch(selectedConversationLoaded({
      ...selectedConversation,
      lastMessage,
      subcampaignId: subcampaign.id,
      subcampaign: {
        ...subcampaign,
        campaign: {
          ...subcampaign.campaign,
          brandProfile
        },
        influencerProfile: subcampaign.creatorProfile
      }
    }));
  };
}

export function fetchConversationCount(campaignId: number): ThunkAction {
  return async (dispatch) => {
    dispatch(setLoadingStatus(true, CONVERSATION_COUNT_LOADING));

    try {
      const conversationCountResponse = await get(MESSENGER.getConversationsCount(campaignId));

      if (conversationCountResponse?.data?.counts) {
        const conversationsCount = conversationCountResponse.data.counts;

        dispatch(conversationsCountLoaded(conversationsCount));
      }
    } catch (error) {
      logError(error);

      throw error;
    } finally {
      dispatch(setLoadingStatus(false, CONVERSATION_COUNT_LOADING));
    }
  };
}

export function fetchConversations(
  campaignId: number,
  page: number,
  params?: { filterByType?: string, filterByState?: string, searchQuery?: string | undefined | null },
  isCountRequestRequired?: boolean
): ThunkAction {
  return async (dispatch) => {
    if (page === 1) {
      dispatch(setLoadingStatus(true));
    }

    const transformedParams = getParams(params);
    const isFilterApplied = !!Object.keys(transformedParams).length;
    const hasUnreadFilter = !!transformedParams.conversationState;

    try {
      const queryString = Querystring.objectToString(snakeCaseKeys({
        campaignId,
        page,
        itemsPerPage: 20,
        ...transformedParams
      }));

      if (isCountRequestRequired) {
        dispatch(fetchConversationCount(campaignId));
      }

      const conversationsResponse = await get(MESSENGER.getPaginatedConversations(queryString));
      const conversations = conversationsResponse.data.data;
      const conversationMeta = conversationsResponse.data.meta;

      if (!conversations.length) {
        dispatch(conversationsLoaded([], true, isFilterApplied, hasUnreadFilter));
        return;
      }

      const queryStringForSubcampaigns = Querystring.objectToString({
        include: ['business_details', 'bank_details', 'billing_address'],
        subcampaign_ids: conversations.map(conversation => conversation.subcampaign.id).filter(subcampaignId => subcampaignId)
      });

      const subcampaignsResponse = await get(`${CAMPAIGNS.getCampaignSubcampaigns(campaignId)}?${queryStringForSubcampaigns}`);
      const subcampaigns = (subcampaignsResponse?.data || []).filter(subcampaign => !isDraft(subcampaign));

      if (!subcampaigns.length) {
        dispatch(conversationsLoaded([], true, isFilterApplied, hasUnreadFilter));
        return;
      }

      const brandProfile = await dispatch(fetchBrandProfile(subcampaigns[0].campaign.brandProfileId, false));

      const subcampaignsMapById = {};
      subcampaigns.forEach(subcampaign => {
        subcampaignsMapById[subcampaign.id] = subcampaign;
      });

      const emptyLastMessage = { // eslint-disable-line no-param-reassign
        attachments: {},
        createdAt: new Date().toISOString(),
        externalId: '',
        metadata: { profile: { id: null, type: '' } },
        senderId: '',
        text: '',
        type: '',
        isEmpty: true
      };

      const result: Array<MessengerConversation> = conversations
        .map((conversation => {
          const subcampaign = subcampaignsMapById[conversation.subcampaign.id];

          if (!subcampaign) {
            return null;
          }

          const lastMessage = conversation.lastMessage || { ...emptyLastMessage };
          return {
            ...conversation,
            lastMessage,
            subcampaignId: subcampaign.id,
            subcampaign: {
              ...subcampaign,
              campaign: {
                ...subcampaign.campaign,
                brandProfile
              },
              influencerProfile: subcampaign.creatorProfile
            }
          } as MessengerConversation;
        }))
        .filter(isDefined);

      if (page === 1) {
        dispatch(conversationsLoaded(result, conversationMeta.pagesTotal > page, isFilterApplied, hasUnreadFilter));
      } else {
        dispatch(loadMoreConversations(result, conversationMeta.pagesTotal > page));
      }
    } catch (error) {
      logError(error);
      dispatch(setLoadingStatus(false));
      throw error;
    }
  };
}

export function fetchAllConversations(campaignId: number): ThunkAction {
  return async (dispatch) => {
    const countUrl = MESSENGER.getConversationsCount(campaignId);
    const queryStringForSubcampaigns = Querystring.objectToString({
      states: ['pending', 'accepted']
    });

    dispatch(showLoading(countUrl));

    try {
      const conversationCountResponse = await get(MESSENGER.getConversationsCount(campaignId));
      const conversationCount = conversationCountResponse?.data?.counts?.overall?.all || 0;

      if (conversationCount === 0) {
        // no conversations available
        return [];
      }

      const url = MESSENGER.getPaginatedConversations(`campaign_id=${campaignId}&page=1&items_per_page=${conversationCount}`);

      const responses = await Promise.all([get(url), get(`${CAMPAIGNS.getCampaignSubcampaigns(campaignId)}?${queryStringForSubcampaigns}`)]);
      const subcampaignsMapById = {};
      const conversations = responses[0]?.data?.data || [];
      const subcampaigns = responses[1]?.data || [];

      subcampaigns.forEach(subcampaign => {
        subcampaignsMapById[subcampaign.id] = subcampaign;
      });

      return conversations.map(conversation => (
        { ...conversation, subcampaign: (subcampaignsMapById[conversation.subcampaign.id] || { creatorProfile: { name: conversation.influencerProfileName } }) }));
    } catch (err) {
      logError(err);
      throw err;
    } finally {
      dispatch(hideLoading(countUrl));
    }
  };
}

export function updateUnreadConversations(campaignId: number, externalId: string): ThunkAction {
  return async (dispatch, getState) => {
    try {
      const conversationsCount = await get(MESSENGER.getConversationsCount(campaignId));
      const conversationState = getState().conversations;
      const conversations = conversationState.list.filter(conversation => conversation.externalId !== externalId);
      const { hasMoreConversations, isFilterApplied, hasUnreadFilter } = conversationState;

      dispatch(conversationsCountLoaded(conversationsCount.data.counts));
      dispatch(conversationsLoaded(conversations, hasMoreConversations, isFilterApplied, hasUnreadFilter));
    } catch (err) {
      logError(err);
    }
  };
}

export function setConversationLastMessage(subcampaignId: number, lastMessage?: MessengerMessage | null): Action {
  return {
    type: SET_LAST_MESSAGE,
    payload: {
      subcampaignId,
      lastMessage
    }
  };
}

export function markConversationAsUnread(conversationExternalId: string): ThunkAction {
  return async () => {
    try {
      await put(MESSENGER.markConversationAsUnread(conversationExternalId));
    } catch (error) {
      logError(error);
      throw error;
    }
  };
}

export default createReducer<ConversationsState, any>('conversations', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [LOADING]: (state, { status }) => ({
    ...state,
    isLoading: status
  }),
  [SELECTED_CONVERSATION_LOADING]: (state, { status }) => ({
    ...state,
    isSelectedConversationLoading: status
  }),
  [LOADED]: (state, { conversations, hasMoreConversations, isFilterApplied, hasUnreadFilter }) => ({
    ...state,
    list: conversations,
    isLoading: false,
    hasMoreConversations,
    isFilterApplied,
    hasUnreadFilter
  }),
  [LOAD_MORE]: (state, { conversations, hasMoreConversations }) => ({
    ...state,
    list: [...state.list, ...conversations],
    isLoading: false,
    hasMoreConversations
  }),
  [SELECTED_CONVERSATION_LOADED]: (state, { conversation }) => ({
    ...state,
    selectedConversation: conversation,
    isSelectedConversationLoading: false
  }),
  [CONVERSATION_COUNT_LOADED]: (state, { count }) => ({
    ...state,
    count
  }),
  [CONVERSATION_COUNT_LOADING]: (state, { status }) => ({
    ...state,
    isConversationCountLoading: status
  }),
  [SET_LAST_MESSAGE]: (state, { subcampaignId, lastMessage }) => {
    const conversationIndex = state.list.findIndex(conversation => conversation.subcampaignId === Number(subcampaignId));

    // Prevent race condition where messages are loaded before conversations. We can't set the last
    // message when we don't have the conversations yet.
    if (conversationIndex === -1) {
      return state;
    }

    const newConversation = { ...state.list[conversationIndex], lastMessage };
    return ({
      ...state,
      list: [
        ...state.list.slice(0, conversationIndex),
        newConversation,
        ...state.list.slice(conversationIndex + 1)
      ]
    });
  }
});
